import { useEffect } from "react";

// Keeps every table's horizontal scrollbar on screen: the wrapper that scrolls sideways is
// capped to the viewport height (so it scrolls vertically inside) and its header stays pinned.
const MIN_H = 240;
const isCheckboxCol = (th) => th.querySelector('input[type="checkbox"]') || th.getBoundingClientRect().width < 44;

const pinColumns = (el) => {
  const table = el.querySelector(":scope > table");
  if (!table || table.dataset.ownPin) return;
  if (table.querySelector("thead th[style*='left:']:not([data-pin]), tbody td[style*='left:']:not([data-pin]), thead th[class*='left-'], tbody td[class*='left-']")) { table.dataset.ownPin = "1"; return; }
  const headRow = table.querySelector("thead tr");
  if (!headRow) return;
  const ths = [...headRow.children];
  const overflow = el.scrollWidth > el.clientWidth + 2;
  let count = 0;
  if (overflow) {
    let data = 0;
    for (const th of ths) { count++; if (!isCheckboxCol(th)) data++; if (data >= 2) break; }
    if (count >= ths.length) count = 0;
  }
  const rows = [...table.querySelectorAll(":scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr")];
  const lefts = []; let acc = 0;
  for (let i = 0; i < count; i++) { lefts.push(acc); acc += ths[i].getBoundingClientRect().width; }
  rows.forEach((tr) => {
    [...tr.children].forEach((cell, i) => {
      if (i < count) {
        cell.style.position = "sticky"; cell.style.left = `${lefts[i]}px`;
        cell.style.zIndex = tr.parentElement.tagName === "THEAD" ? "25" : "5";
        cell.style.background = "hsl(var(--card))";
        cell.style.boxShadow = i === count - 1 ? "inset -1px 0 0 hsl(var(--border))" : "";
        cell.dataset.pin = "1";
      } else if (cell.dataset.pin) {
        cell.style.position = ""; cell.style.left = ""; cell.style.zIndex = ""; cell.style.background = ""; cell.style.boxShadow = "";
        delete cell.dataset.pin;
      }
    });
  });
};

const fit = () => {
  // Touch devices (phones / tablets, incl. Desktop view): never cap the height — window.innerHeight there is the *visual*
  // viewport, which shrinks on pinch-zoom and collapsed tables to a 4-row inner scroller. Let the page scroll instead.
  const touch = window.matchMedia?.("(pointer: coarse)")?.matches || navigator.maxTouchPoints > 0;
  const vh = document.documentElement.clientHeight || window.innerHeight;   // layout viewport, stable under zoom
  document.querySelectorAll("main div:has(> table)").forEach((el) => {
    if (el.closest("[role=dialog],[data-no-fit]")) return;
    const cs = getComputedStyle(el);
    if (!/(auto|scroll)/.test(cs.overflowX) && !/(auto|scroll)/.test(cs.overflow)) return;
    if (!el.closest("[data-no-pin]")) pinColumns(el);
    if (touch) {
      if (el.dataset.fit === "1") { el.style.maxHeight = ""; el.style.overflowY = ""; delete el.dataset.fit; }
      return;
    }
    if (el.style.maxHeight && el.dataset.fit !== "1") return; // page already manages its own height
    const top = el.getBoundingClientRect().top + window.scrollY;
    const h = Math.max(MIN_H, vh - top - 24);
    el.dataset.fit = "1";
    el.style.maxHeight = `${h}px`;
    el.style.overflowY = "auto";
    const thead = el.querySelector(":scope > table > thead");
    if (thead && !thead.dataset.fit) {
      thead.dataset.fit = "1";
      thead.style.position = "sticky"; thead.style.top = "0"; thead.style.zIndex = "20";
      thead.style.background = "hsl(var(--card))";
    }
  });
};

export function useFitTables() {
  useEffect(() => {
    let t, last = 0;
    const run = () => {
      clearTimeout(t);
      const wait = Math.max(60, 200 - (Date.now() - last));
      t = setTimeout(() => { last = Date.now(); fit(); }, wait);
    };
    const mo = new MutationObserver(run);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", run);
    run();
    return () => { mo.disconnect(); window.removeEventListener("resize", run); clearTimeout(t); };
  }, []);
}
